import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, Image, StyleSheet, PanResponder } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Svg, { Path } from 'react-native-svg';
import { AppView, Button, ButtonGroup, rem, SettingsItem, Spacer } from 'design-system';
import { IconBoldCamera, IconBoldImage, IconEdit, IconTrash } from 'design-system/icons';
import IconPlus from 'design-system/icons/IconPlus';
import Modal from 'utils/packages/Modal';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import SectionHeader from 'design-system/SectionHeader';
import Text from 'design-system/Text';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { openCamera, openImageLibrary } from 'features/transactions/utils';
const strings = {
    title: {
        draw: 'Add your signature to the transfer',
        upload: 'Upload your signature to the transfer',
    },
    subTitle: {
        draw: 'Some providers need handwritten signature. So we’ll use this to sign transfer documents for you where needed',
        upload: 'Some providers need a signature to authorise your transfer. Sign a piece of paper and take a photo of it',
    },
    addSignature: 'Add signature',
    uploadSignature: 'Upload your signature',
    yourSignature: 'Your signature',
    drawSignature: 'Draw signature',
    drawSignatureDescription: 'You can sign with your usual signature, name or even initials. Don’t worry if it isn’t perfect.',
    uploadedFile: 'Uploaded file',
    review: 'Review',
    edit: 'Edit',
    clear: 'Clear',
    save: 'Save',
    doNotWorry: 'We won’t use this for any other reasons',
    continue: 'Continue',
    takePhoto: 'Take photo',
    choosePhoto: 'Choose photo',
    cameraPermissionTitle: 'Emma requires Camera permission',
    cameraPermissionMessage: 'To take a photo of your signature, please allow camera access.',
};
const SignatureSvg = React.memo(({ paths, ...svgProps }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    return (<Svg style={styles.flex} {...svgProps}>
      {paths.map((d, i) => (<Path key={`${i}`} d={d} stroke={colors.text.primary} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" fill="none"/>))}
    </Svg>);
});
const DrawSignatureModalContent = ({ onSave }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [paths, setPaths] = useState([]);
    const currentPath = useRef('');
    const [canvasWidth, setCanvasWidth] = useState(0);
    const panResponder = useRef(PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onStartShouldSetPanResponderCapture: () => true,
        onMoveShouldSetPanResponderCapture: () => true,
        onPanResponderTerminationRequest: () => false,
        onPanResponderGrant: (evt) => {
            const { locationX, locationY } = evt.nativeEvent;
            currentPath.current = `M${locationX},${locationY}`;
            setPaths((prev) => [...prev, currentPath.current]);
        },
        onPanResponderMove: (evt) => {
            const { locationX, locationY } = evt.nativeEvent;
            currentPath.current += ` L${locationX},${locationY}`;
            setPaths((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = currentPath.current;
                return updated;
            });
        },
    })).current;
    const onCanvasLayout = useCallback((event) => {
        const { width } = event.nativeEvent.layout;
        setCanvasWidth(width);
    }, []);
    const handleClear = useCallback(() => {
        setPaths([]);
        currentPath.current = '';
    }, []);
    const hasDrawablePath = useMemo(() => paths.some((path) => path.includes(' L')), [paths]);
    const handleSave = useCallback(() => {
        const drawablePaths = paths.filter((path) => path.includes(' L'));
        if (canvasWidth && drawablePaths.length > 0) {
            onSave({
                paths: drawablePaths,
                width: canvasWidth,
                height: canvasWidth / 1.72,
            });
            Modal.hide();
        }
    }, [canvasWidth, onSave, paths]);
    return (<View style={styles.drawSignatureModalContent}>
      <Text TextThin-14 Secondary centered>
        {strings.drawSignatureDescription}
      </Text>
      <View style={styles.drawCanvas} {...panResponder.panHandlers} onLayout={onCanvasLayout}>
        <SignatureSvg paths={paths}/>
      </View>
      <Button brandReversed hug square leftIcon={<IconTrash height={16} width={16} color={colors.text.textOnBrand}/>} title={strings.clear} onPress={handleClear} containerStyle={styles.drawClearButton}/>
      <Button brand title={strings.save} onPress={handleSave} disabled={!hasDrawablePath}/>
    </View>);
};
const DrawSignatureScreenContent = ({ signature, setSignature }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const startDrawProcess = useCallback(() => {
        Modal.show(<DrawSignatureModalContent onSave={setSignature}/>, { title: strings.drawSignature });
    }, [setSignature]);
    if (signature == null) {
        return (<TouchableBounceCard noMarginTop onPress={startDrawProcess} onDark>
        <SettingsItem disabled title={strings.addSignature} description={strings.doNotWorry} icon={<IconPlus colorKey="brand"/>}/>
      </TouchableBounceCard>);
    }
    const { paths, width, height } = signature;
    return (<View>
      <SectionHeader horizontalMargins>{strings.yourSignature}</SectionHeader>
      <View style={[{ width, aspectRatio: width / height }, styles.drawSignatureViewer]}>
        <SignatureSvg paths={paths} viewBox={`0 0 ${width} ${height}`}/>
      </View>
      <Button brandReversed hug square leftIcon={<IconEdit height={16} width={16} color={colors.text.textOnBrand}/>} title={strings.edit} onPress={startDrawProcess} containerStyle={styles.drawEditButton}/>
    </View>);
};
const drawnToSvgString = ({ paths, width, height }) => {
    const pathElements = paths
        .map((d) => `<path d="${d}" stroke="#000000" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`)
        .join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">${pathElements}</svg>`;
};
const UploadSignatureScreenContent = ({ signature, setSignature }) => {
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const handleTakePhoto = useCallback(() => {
        Modal.hide(() => {
            openCamera((assets) => {
                const asset = assets?.[0];
                if (asset?.uri && asset.width && asset.height)
                    setSignature({ uri: asset.uri, mimeType: asset.type, width: asset.width, height: asset.height });
            });
        });
    }, [setSignature]);
    const handleChoosePhoto = useCallback(() => {
        Modal.hide(() => {
            openImageLibrary(1, (assets) => {
                const asset = assets?.[0];
                if (asset?.uri && asset.width && asset.height)
                    setSignature({
                        uri: asset.uri,
                        fileName: asset.fileName,
                        mimeType: asset.type,
                        width: asset.width,
                        height: asset.height,
                    });
            });
        });
    }, [setSignature]);
    const startUploadProcess = useCallback(() => {
        Modal.show(<>
        <Spacer.H8 />
        <SettingsItem title={strings.takePhoto} onPress={handleTakePhoto} icon={<IconBoldCamera gradientKey="deepPink"/>}/>
        <SettingsItem title={strings.choosePhoto} onPress={handleChoosePhoto} icon={<IconBoldImage gradientKey="orange"/>}/>
      </>, { title: strings.uploadSignature, containerStyle: { paddingHorizontal: 0 } });
    }, [handleTakePhoto, handleChoosePhoto]);
    const viewSignature = useMemo(() => signature
        ? () => navigation.navigate('ViewImageScreen', { source: { uri: signature.uri }, title: signature.fileName })
        : undefined, [signature]);
    if (signature == null) {
        return (<TouchableBounceCard noMarginTop onPress={startUploadProcess} onDark>
        <SettingsItem disabled title={strings.uploadSignature} description={strings.doNotWorry} icon={<IconBoldImage colorKey="brand"/>}/>
      </TouchableBounceCard>);
    }
    return (<View>
      <SectionHeader horizontalMargins>{strings.uploadedFile}</SectionHeader>
      <View style={styles.uploadedSignature}>
        <View style={styles.uploadedSignatureImageContainer}>
          <Image source={{ uri: signature.uri }} style={StyleSheet.absoluteFillObject} resizeMode="cover"/>
        </View>
        <View style={styles.uploadedSignatureTextContainer}>
          <Text Text-16 numberOfLines={1}>
            {signature.fileName ?? strings.yourSignature}
          </Text>
          <TouchableBounce onPress={viewSignature}>
            <Text TextThin-14 Primary>
              {strings.review}
            </Text>
          </TouchableBounce>
        </View>
        <TouchableBounce onPress={startUploadProcess}>
          <IconEdit colorKey="secondary"/>
        </TouchableBounce>
      </View>
    </View>);
};
const ProductTransferSignatureScreen = ({ navigation, route }) => {
    const colors = useColors();
    const { type, provider, accountId, balance, transferTaxYear, ...identification } = route.params;
    const accountType = accountId ? 'JISA' : 'ISA';
    const { isaSignatureType, jisaSignatureType } = provider;
    const jisaOrIsaSignatureType = accountType === 'JISA' ? jisaSignatureType : isaSignatureType;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const [drawn, setDrawn] = useState(null);
    const [pic, setPic] = useState(null);
    const handleContinue = useCallback(async () => {
        navigation.navigate('ProductTransferConfirmDetails', {
            type,
            provider,
            accountId,
            balance,
            transferTaxYear,
            ...identification,
            signatureSvg: drawn ? drawnToSvgString(drawn) : undefined,
            uploadInfo: pic
                ? {
                    path: pic.uri,
                    width: pic.width,
                    height: pic.height,
                    mimeType: pic.mimeType,
                }
                : undefined,
        });
    }, [accountId, balance, drawn, pic, identification, provider, transferTaxYear, type]);
    const hasSignature = jisaOrIsaSignatureType === 'ELECTRONIC' ? drawn != null : pic != null;
    const signatureType = jisaOrIsaSignatureType === 'ELECTRONIC' ? 'draw' : 'upload';
    return (<AppView avoidHeader>
      <ScreenInfoHeader title={strings.title[signatureType]} subTitle={strings.subTitle[signatureType]}/>
      {signatureType === 'draw' && <DrawSignatureScreenContent signature={drawn} setSignature={setDrawn}/>}
      {signatureType === 'upload' && <UploadSignatureScreenContent signature={pic} setSignature={setPic}/>}
      <ButtonGroup type="floating">
        <Text TextThin-14 Secondary centered>
          We’ll share this with your {accountType} provider
        </Text>
        <Button brand title={strings.continue} onPress={handleContinue} disabled={!hasSignature}/>
      </ButtonGroup>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    drawSignatureModalContent: {
        gap: rem(16),
        paddingTop: rem(16),
    },
    drawCanvas: {
        width: '100%',
        aspectRatio: 1.72,
        backgroundColor: colors.background.dark,
        borderRadius: rem(16),
        overflow: 'hidden',
    },
    drawClearButton: {
        alignSelf: 'flex-end',
        marginBottom: rem(16),
    },
    drawSignatureViewer: {
        margin: rem(16),
        borderRadius: rem(16),
        overflow: 'hidden',
        backgroundColor: colors.cards.onDark,
    },
    drawEditButton: {
        alignSelf: 'flex-end',
        marginRight: rem(16),
    },
    uploadedSignature: {
        marginTop: rem(16),
        marginHorizontal: rem(16),
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(16),
        backgroundColor: colors.cards.onDark,
        borderRadius: rem(16),
    },
    uploadedSignatureImageContainer: {
        height: rem(44),
        width: rem(44),
        borderRadius: rem(12),
        borderWidth: rem(1),
        borderColor: colors.borders.iconStroke,
        overflow: 'hidden',
    },
    uploadedSignatureTextContainer: {
        flex: 1,
        alignItems: 'flex-start',
    },
    flex: {
        flex: 1,
    },
}));
export default ProductTransferSignatureScreen;
